/**
 * Sticky Cart Handler - Pure Vanilla JS
 * HTML: snippets/sticky-cart-bar.liquid
 * Ultra-optimized, dynamic, high-performance
 */

(function() {
  'use strict';

  // CONFIG
  const stickyRoot = document.querySelector('.sticky_cart[data-bundle-price]');
  const rootBundlePrice = stickyRoot ? parseInt(stickyRoot.dataset.bundlePrice || '0', 10) : 0;
  const rootMandatoryLimit = stickyRoot ? parseInt(stickyRoot.dataset.mandatoryProducts || '0', 10) : 0;
  const cfg = {
    bundlePrice: rootBundlePrice || parseInt(window.STICKY_CART_CONFIG?.bundlePrice || 0, 10) || 0,
    mandatoryLimit: rootMandatoryLimit || parseInt(window.STICKY_CART_CONFIG?.mandatoryLimit || 0, 10) || 3,
  };
  
  // CACHE: DOM elements
  const el = {};
  const getEl = id => el[id] || (el[id] = document.getElementById(id));

  // STATE
  let selected = [];
  let updateTimer = null;
  
  // HELPERS
  const fmt = (paise) => (paise / 100).toFixed(0);
  const fmtDec = (paise) => (paise / 100).toFixed(2);
  const formatCurrency = (paise) => `₹${fmt(paise)}`;
  const debounce = (fn, ms = 100) => {
    return () => {
      clearTimeout(updateTimer);
      updateTimer = setTimeout(fn, ms);
    };
  };

  // GET IMAGE
  const getImg = (item) => {
    // .product-images-slider is the actual image container rendered by
    // new-flexi-box-bundle-image-wrapper.liquid for this specific card —
    // check it first so we never accidentally pick up an unrelated <img>
    // (badge icon, another card's leaked reference, etc.) from this item.
    const sels = ['.product-images-slider img', '.product-image img', '.product-image picture img', 'img[alt]', 'img'];
    for (const s of sels) {
      const img = item.querySelector(s);
      if (img?.src) return img.src;
    }
    return '';
  };

  // POPUP
  const closePopup = () => {
    getEl('flexi-popup').style.display = 'none';
    getEl('flexi-popup-overlay').style.display = 'none';
    document.body.classList.remove('flexi-popup-open');
  };

  const syncBundlePriceText = () => {
    const titlePrice = getEl('flexi-price-amount');
    if (titlePrice) {
      titlePrice.textContent = formatCurrency(cfg.bundlePrice);
    }
    const titlePriceDesktop = getEl('flexi-price-amount-desktop');
    if (titlePriceDesktop) {
      titlePriceDesktop.textContent = formatCurrency(cfg.bundlePrice);
    }
  };

  // UPDATE SELECTED PRODUCTS
  const update = () => {
    selected = [];
    let total = 0;
    const seenItems = new Set();

    // Get all quantity inputs with values > 0
    document.querySelectorAll('.quantity-input').forEach(quantityInput => {
      const qty = Math.min(parseInt(quantityInput.value || 0, 10), 1);
      // Skip if quantity is 0 or less
      if (qty <= 0) return;

      const item = quantityInput.closest('.collection-item');
      if (!item) return;
      // Guard against double-counting the same product (e.g. a stray
      // duplicate .quantity-input on the page pointing at the same card).
      if (seenItems.has(item)) return;
      seenItems.add(item);

      const checkbox = item.querySelector('input[type="checkbox"]');
      if (!checkbox) return;

      const price = parseInt(checkbox.dataset.price || 0);
      const title = checkbox.value.split('/')[0];
      const image = getImg(item);

      selected.push({ title, image, price, checkbox, quantityInput });
      total += price;
    });

    render(total);
  };

  // RENDER STICKY BAR
  const render = (total) => {
    const count = selected.length;
    const rem = cfg.mandatoryLimit - count;
    const title = getEl('flexi-title');
    const countEl = getEl('flexi-count');
    const msgEl = getEl('flexi-message');
    const msgElDesktop = getEl('flexi-message-desktop');
    const dealPrice = fmt(cfg.bundlePrice);

    // Keep the title visible on mobile at all times (matches the
    // desktop title, which is never swapped out for a count either).
    title.style.display = 'block';
    countEl.style.display = 'none';

    if (count === 0) {
      // Initial state - NO items
      countEl.textContent = '0/3 Added'; // Reset to prevent stale text
      msgEl.innerHTML = `Add Any <span id="flexi-mandate">${cfg.mandatoryLimit}</span> Products`;
      if (msgElDesktop) msgElDesktop.textContent = `Add ${cfg.mandatoryLimit} more products in the Box`;
    } else {
      // Items added
      countEl.textContent = `${count}/${cfg.mandatoryLimit} Added`;
      msgEl.textContent = count < cfg.mandatoryLimit
        ? `Add ${rem} More Item${rem > 1 ? 's' : ''} to Unlock ₹${dealPrice}`
        : `You've Unlocked ₹${dealPrice} Deal Price`;
      if (msgElDesktop) {
        msgElDesktop.textContent = count < cfg.mandatoryLimit
          ? `Add ${rem} more product${rem > 1 ? 's' : ''} in the Box`
          : `Ready! Added to your Box`;
      }
    }

    // Images
    const imgBox = getEl('flexi-product-images');
    const lock = getEl('flexi-lock-icon');
    
    if (count === 0) {
      imgBox.style.display = 'none';
      lock.style.display = 'block';
    } else {
      imgBox.style.display = 'flex';
      lock.style.display = 'none';
      imgBox.innerHTML = selected.map((p, idx) => `
        <div class="flexi-sticky-thumb">
          <img src="${p.image}" alt="${p.title}" class="flexi-sticky-product-img" onerror="this.src='https://via.placeholder.com/50'">
          <button type="button" class="flexi-sticky-thumb-remove" data-remove-idx="${idx}" aria-label="Remove ${p.title}">
            <svg width="10" height="11" viewBox="0 0 10 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 2.75H9M3.5 2.75V1.5C3.5 1.22386 3.72386 1 4 1H6C6.27614 1 6.5 1.22386 6.5 1.5V2.75M4.25 5.125V8.125M5.75 5.125V8.125M1.75 2.75L2.1875 9.125C2.20724 9.61494 2.60506 10 3.09545 10H6.90455C7.39494 10 7.79276 9.61494 7.8125 9.125L8.25 2.75" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      `).join('') + Array(Math.max(0, cfg.mandatoryLimit - count))
        .fill('<div class="flexi-sticky-empty-slot"></div>')
        .join('');
    }

    // Right section
    const right = getEl('flexi-cart-right');
    const savingsSection = getEl('flexi-savings-section');
    const itemValueSection = getEl('flexi-item-value-section');
    const isComplete = count === cfg.mandatoryLimit;
    
    if (count === 0) {
      right.style.display = 'none';
    } else {
      right.style.display = 'flex';

      // Show savings when all items checked, otherwise show item value
      if (isComplete) {
        itemValueSection.style.display = 'none';
        savingsSection.style.display = 'block';
        
        const savings = total - cfg.bundlePrice;
        getEl('flexi-savings-value').textContent = fmt(Math.max(0, savings));
        getEl('flexi-deal-price-value').textContent = fmtDec(cfg.bundlePrice);
        getEl('flexi-combined-price-value').textContent = fmtDec(total);
      } else {
        savingsSection.style.display = 'none';
        itemValueSection.style.display = 'block';
        getEl('flexi-price-value').textContent = fmtDec(total);
      }
    }

    // Pay button only enabled when all mandatory items selected
    const payNowBtn = getEl('flexi-pay-now');
    payNowBtn.disabled = count !== cfg.mandatoryLimit;
    payNowBtn.style.opacity = count === cfg.mandatoryLimit ? '1' : '0.5';
    payNowBtn.style.pointerEvents = count === cfg.mandatoryLimit ? 'auto' : 'none';
    payNowBtn.classList.toggle('ready-to-checkout', count === cfg.mandatoryLimit);

    if (count === 0) closePopup();

    renderPopup(total);
  };

  // RENDER POPUP
  const renderPopup = (total) => {
    const count = selected.length;
    const rem = cfg.mandatoryLimit - count;
    const imgBox = getEl('flexi-popup-images');

    // Update popup title with count
    const popupTitle = getEl('flexi-popup-title');
    if (popupTitle) {
      popupTitle.textContent = `Your Box (${count}/${cfg.mandatoryLimit})`;
    }

    // Render items in order (first added shows first)
    imgBox.innerHTML = selected.map((p, idx) => `
      <div class="flexi-popup-img-wrapper">
        <div class="flexi-popup-img-container">
          <img src="${p.image}" alt="${p.title}" class="flexi-popup-product-img" onerror="this.src='https://via.placeholder.com/80'">
          <button class="flexi-popup-img-close" type="button" data-remove data-index="${idx}" title="Remove">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6L18 18"/></svg>
          </button>
        </div>
        <div class="flexi-popup-product-info">
          <div class="flexi-popup-product-title">${p.title}</div>
          <div class="flexi-popup-product-price">₹${fmtDec(p.price)}</div>
        </div>
      </div>
    `).join('') + Array(Math.max(0, cfg.mandatoryLimit - count))
      .fill('<div class="flexi-popup-empty-slot">+</div>')
      .join('');

    // Bill Summary
    const uTotal = selected.reduce((sum, p) => sum + p.price, 0);
    const isComplete = count === cfg.mandatoryLimit;
    
    getEl('flexi-summary-item-count').textContent = count;
    getEl('flexi-summary-subtotal').textContent = `₹${fmtDec(uTotal)}`;
    
    // Show save row only if all items selected
    const saveRow = getEl('flexi-summary-save');
    if (isComplete) {
      const savings = uTotal - cfg.bundlePrice;
      getEl('flexi-summary-save-amount').textContent = `₹${fmt(Math.max(0, savings))}`;
      saveRow.style.display = 'flex';
      getEl('flexi-summary-total').textContent = `₹${fmtDec(cfg.bundlePrice)}`;
    } else {
      saveRow.style.display = 'none';
      getEl('flexi-summary-total').textContent = `₹${fmtDec(uTotal)}`;
    }
    
    // Item Value and Savings Display (bottom section)
    const savingsSection = getEl('flexi-popup-savings-section');
    const itemValueSection = getEl('flexi-popup-item-value-section');
    
    if (isComplete) {
      itemValueSection.style.display = 'none';
      savingsSection.style.display = 'block';
      
      const savings = uTotal - cfg.bundlePrice;
      getEl('flexi-popup-savings-value').textContent = fmt(Math.max(0, savings));
      getEl('flexi-popup-deal-price-value').textContent = fmtDec(cfg.bundlePrice);
      getEl('flexi-popup-combined-price-value').textContent = fmtDec(uTotal);
    } else {
      savingsSection.style.display = 'none';
      itemValueSection.style.display = 'block';
      getEl('flexi-popup-price-value').textContent = fmtDec(uTotal);
    }

    // Popup pay button only enabled when all mandatory items selected
    const popupPayBtn = getEl('flexi-popup-pay-now');
    popupPayBtn.disabled = count !== cfg.mandatoryLimit;
    popupPayBtn.style.opacity = count === cfg.mandatoryLimit ? '1' : '0.5';
    popupPayBtn.style.pointerEvents = count === cfg.mandatoryLimit ? 'auto' : 'none';
    popupPayBtn.classList.toggle('ready-to-checkout', count === cfg.mandatoryLimit);
    
    // Show arrow in popup pay button when all items selected
    const popupPayArrow = getEl('flexi-popup-pay-arrow');
    if (popupPayArrow) {
      popupPayArrow.style.display = count === cfg.mandatoryLimit ? 'inline-block' : 'none';
    }

    // Add click handlers to empty slots
    document.querySelectorAll('.flexi-popup-empty-slot').forEach(slot => {
      slot.addEventListener('click', () => {
        closePopup();
      });
    });
  };

  // EVENTS
  const init = () => {
    const upd = debounce(update);

    // Global click handler to prevent drawer opening
    document.addEventListener('click', (e) => {
      const isPopupOpen = getEl('flexi-popup').style.display === 'block';
      // Allow close button, empty slot, and internal popup elements to work
      if (isPopupOpen && e.target.closest('.flexi-popup') && !e.target.closest('.flexi-popup-close') && !e.target.closest('.flexi-popup-empty-slot') && !e.target.closest('#flexi-popup-pay-now')) {
        e.stopPropagation();
      }
    }, true); // Use capture phase to intercept before other handlers

    // Close - prevent propagation
    getEl('flexi-popup-close').addEventListener('click', (e) => {
      e.stopImmediatePropagation();
      closePopup();
    });
    getEl('flexi-popup-overlay').addEventListener('click', (e) => {
      e.stopImmediatePropagation();
      closePopup();
    });

    // Remove item from popup - click the quantity-minus button of same product
    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('.flexi-popup-img-close');
      if (!closeBtn) return;

      e.stopPropagation();
      e.preventDefault();

      const idx = parseInt(closeBtn.dataset.index);
      if (isNaN(idx) || idx < 0 || idx >= selected.length) return;

      const item = selected[idx];
      if (!item?.quantityInput) return;

      // Find the collection-item and then find its quantity-minus button
      const collectionItem = item.quantityInput.closest('.collection-item');
      if (!collectionItem) return;

      const minusBtn = collectionItem.querySelector('.quantity-minus');
      if (minusBtn) {
        minusBtn.click();
      }
    }, true);

    // Remove item from the sticky bar's own thumbnail row - same approach as
    // the popup's remove button above (click the quantity-minus of that product).
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.flexi-sticky-thumb-remove');
      if (!removeBtn) return;

      e.stopPropagation();
      e.preventDefault();

      const idx = parseInt(removeBtn.dataset.removeIdx);
      if (isNaN(idx) || idx < 0 || idx >= selected.length) return;

      const item = selected[idx];
      if (!item?.quantityInput) return;

      const collectionItem = item.quantityInput.closest('.collection-item');
      if (!collectionItem) return;

      const minusBtn = collectionItem.querySelector('.quantity-minus');
      if (minusBtn) {
        minusBtn.click();
      }
    }, true);

    // Pay - prevent propagation and prevent cart drawer
    getEl('flexi-pay-now').addEventListener('click', async (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (typeof window.flexiStartCheckout === 'function') {
        await window.flexiStartCheckout();
      }
    });
    getEl('flexi-popup-pay-now').addEventListener('click', async (e) => {
      e.stopPropagation();
      e.preventDefault();
      closePopup();
      if (typeof window.flexiStartCheckout === 'function') {
        await window.flexiStartCheckout();
      }
    });

    // Product changes
    document.addEventListener('change', (e) => {
      if ((e.target.type === 'checkbox' && e.target.closest('.collection-item')) || 
          e.target.classList.contains('quantity-input')) {
        // Direct update for checkbox changes, debounced for quantity
        if (e.target.type === 'checkbox') {
          update();
        } else {
          upd();
        }
      }
    });

    // Quantity +/- buttons - with delay to allow DOM update
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('quantity-plus') || 
          e.target.classList.contains('quantity-minus')) {
        // Wait for DOM to update before recalculating
        setTimeout(upd, 50);
      }
    });

    // Listen for quantity input value changes (for programmatic updates)
    document.addEventListener('input', (e) => {
      if (e.target.classList.contains('quantity-input')) {
        upd();
      }
    });
  };

  // AUTO-INIT
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  syncBundlePriceText();

  // EXPOSE API
  window.stickyCart = { update, closePopup };
})();
